import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { ArrowBendDoubleUpRight } from "@phosphor-icons/react/ArrowBendDoubleUpRight";
import { ArrowsClockwise } from "@phosphor-icons/react/ArrowsClockwise";
import { ChartLineUp } from "@phosphor-icons/react/ChartLineUp";
import { CloudArrowUp } from "@phosphor-icons/react/CloudArrowUp";
import { FilePlus } from "@phosphor-icons/react/FilePlus";
import { Globe } from "@phosphor-icons/react/Globe";
import { Handshake } from "@phosphor-icons/react/Handshake";
import { Heartbeat } from "@phosphor-icons/react/Heartbeat";
import { Layout } from "@phosphor-icons/react/Layout";
import { Leaf } from "@phosphor-icons/react/Leaf";
import { Lightning } from "@phosphor-icons/react/Lightning";
import { Link } from "@phosphor-icons/react/Link";
import { ListMagnifyingGlass } from "@phosphor-icons/react/ListMagnifyingGlass";
import { MapTrifold } from "@phosphor-icons/react/MapTrifold";
import { PaintBrush } from "@phosphor-icons/react/PaintBrush";
import { PiggyBank } from "@phosphor-icons/react/PiggyBank";
import { Quotes } from "@phosphor-icons/react/Quotes";
import { Tag } from "@phosphor-icons/react/Tag";
import { UsersThree } from "@phosphor-icons/react/UsersThree";
import { mapPalettes } from "mangomap-ui/src/components/map-field";

import type { BenefitsContent } from "~landing/components/home/benefits";
import type { FaqsContent } from "~landing/components/home/faqs";
import type { FeatureHighlightsContent } from "~landing/components/home/feature-highlights";
import type { HeroContent } from "~landing/components/home/hero";
import type { HowItWorksContent } from "~landing/components/home/how-it-works";
import type { PricingTeaserContent } from "~landing/components/home/pricing-teaser";
import type { StartBuildingContent } from "~landing/components/home/start-building";
import type { TestimonialsContent } from "~landing/components/home/testimonials";
import type { UseCasesContent } from "~landing/components/home/use-cases";

// Environment solutions copy, rendered through the shared home section template.

const hero: HeroContent = {
	eyebrow: "Environmental Mapping",
	icon: <Leaf />,
	heading: "Every dataset in. One link out.",
	subHeading:
		"Mango is the publishing layer for finished environmental work. Publish it with no developer, no server, no GIS stack. Funders just open the link in a browser.",
	ctaButton: "Start Your 30-Day Free Trial",
	keyObjection: "30-day free trial. No credit card needed.",
	socialProof:
		"“We swapped static PDF reports for a live portal funders can open. It changed how the grant review went.” Used at Ridge to Reefs and EPR.",
	socialProofLabel: "Used by teams like:",
	image: "/app-assets/images/hero-map.jpg",
	// Hero MapField background — contour variant. Full control surface:
	//   seed        change to get a different terrain shape
	//   palette     mapPalettes.{ bathymetric | topoPaper | indigoInk | forest | slate | onDark }
	//               .line = regular isolines, .index = emphasised index contours
	//   strokeWidth line thickness
	//   octaves     fractal-noise detail; higher = more intricate ridges/valleys
	//   levels      number of contour lines
	//   cols / rows sampling grid resolution; higher = smoother, denser lines
	//   indexEvery  every Nth line is drawn as an emphasised index contour
	//   aspect      canvas aspect ratio (number or "16 / 9")
	//   animate / duration  sliding emphasis sweep and its period (seconds)
	//   opacity     overall background opacity
	mapField: {
		seed: "mango-environment",
		variant: "contour",
		palette: mapPalettes.bathymetric,
		strokeWidth: 0.5,
		octaves: 3,
		levels: 10,
		cols: 48,
		rows: 48,
		indexEvery: 5,
		aspect: "16 / 9",
		animate: true,
		duration: 2,
		opacity: 0.12,
	},
	customers: [
		{
			customer: "Think City",
			src: "/app-assets/images/customer-thinkcity.png",
		},
		{
			customer: "WRHA",
			src: "/app-assets/images/customer-wrha.svg",
		},
		{
			customer: "Swift Aerial Surveys",
			src: "/app-assets/svg/customer-swift.svg",
		},
		{
			customer: "GIS4AG",
			src: "/app-assets/svg/customer-gis4ag.svg",
		},
		{
			customer: "Sensar",
			src: "/app-assets/images/customer-sensar.png",
		},
		{
			customer: "Enerko",
			src: "/app-assets/svg/customer-enerko.svg",
		},
	],
};

const benefits: BenefitsContent = {
	heading: "Publish compelling maps without the overhead or the licence",
	subHeading:
		"Turn drone imagery, field data, and open sources into a portal funders can read, on pricing that fits a grant cycle.",
	cards: [
		{
			eyebrow: "Save",
			icon: <PiggyBank />,
			title: "Pricing that fits a grant cycle",
			description:
				"Fixed, predictable plans you can write into a grant budget and defend to a funder. No metered usage, no costs that move once the project is underway.",
			image: "/app-assets/images/ink-pricing-invoice.png",
		},
		{
			eyebrow: "Speed",
			icon: <Lightning />,
			title: "Live in days, not a build cycle",
			description:
				"Publish a portal from your finished data without a developer or a web build. The time goes into the science and the fieldwork, not the stack.",
			image: "/app-assets/images/ink-import-to-live-speed.png",
		},
		{
			eyebrow: "Sync",
			icon: <ArrowsClockwise />,
			title: "Update across the whole project life",
			description:
				"Refresh the portal as new drone flights and field data come in. Funders and clients see the current picture, not last season's snapshot.",
			image: "/app-assets/images/ink-pricing-fixed-updates.png",
		},
		{
			eyebrow: "Team",
			icon: <UsersThree />,
			title: "Made for the people who fund the work",
			description:
				"Funders, clients, and community stakeholders open a link and explore the map themselves. No GIS software, no training, no walkthrough from you.",
			image: "/app-assets/images/ink-map-stakeholder-view-pricing.png",
		},
	],
};

const useCases: UseCasesContent = {
	heading: "Environmental teams already doing this with Mango",
	subHeading:
		"From sustainability education to urban analytics — real teams, real maps.",
	cards: [
		{
			id: "use-case-sustainability",
			eyebrow: "Sustainability",
			icon: <Leaf />,
			title: "Turn data into a sustainability tool",
			description:
				"SESC in São Paulo turned georeferenced data into a hands-on sustainability education tool for the community of Guarulhos.",
			image:
				"/app-assets/images/articles/mangomap-as-a-tool-for-sustainability/hero.jpg",
			link: "/case-studies/mangomap-as-a-tool-for-sustainability",
		},
		{
			id: "use-case-public-health",
			eyebrow: "Public health",
			icon: <Heartbeat />,
			title: "Map the problem into a public story",
			description:
				"A University of Manitoba team mapped Winnipeg's food deserts, turning socioeconomic data into a story the public could explore.",
			image:
				"/app-assets/images/articles/winnipeg-food-atlas/nrd-d6tu_l3chle-unsplash.jpg",
			link: "/case-studies/winnipeg-food-atlas",
		},
		{
			id: "use-case-urban-analytics",
			eyebrow: "Urban analytics",
			icon: <ChartLineUp />,
			title: "Democratise city data",
			description:
				"Malaysian urban-impact agency Think City used Mango to open up city data through its public Urban Analytics portal.",
			image: "/app-assets/images/articles/think-city-urban-analytics/hero.png",
			link: "/case-studies/think-city-urban-analytics",
		},
		{
			id: "use-case-field-survey",
			eyebrow: "Energy & field survey",
			icon: <Lightning />,
			title: "One live map for the whole field team",
			description:
				"GIS consultancy DavSeis kept Europe's biggest seismic survey — 300 people working nationwide — running from one always-current map.",
			image:
				"/app-assets/images/articles/europe-biggest-seismic-survey/hero.jpg",
			link: "/case-studies/europe-biggest-seismic-survey",
		},
	],
};

const testimonials: TestimonialsContent = {
	eyebrow: "Success stories",
	icon: <Quotes />,
	reviews: [
		{
			id: "REV-1",
			author: "Dr. Ceelia Leong",
			title: "Think City",
			logo: "/app-assets/images/customer-thinkcity.png",
			comment:
				"We chose Mango for our map visualisation and narrative because of its simplistic and straightforward nature. The interface is light and easy to navigate on desktop or mobile, making it useful to both our on-ground survey teams and the active public.",
		},
		{
			id: "REV-2",
			author: "Vlad Cristian Nita",
			title: "S3 Seismic",
			logo: "/app-assets/images/customer-s3seismic.svg",
			comment:
				"With an online tool like Mango, it’s easier to keep a track record of any changes. Everybody can then report to the GIS office and the GIS office can create maps.",
		},
		{
			id: "REV-3",
			author: "Dr. Chris Green",
			title: "Snr Epidemiologist, WRHA",
			logo: "/app-assets/images/customer-wrha.svg",
			comment:
				"Love Mango... compared to ArcGIS online, it provides better visual mapping and a straightforward pricing model.",
		},
	],
};

const howItWorks: HowItWorksContent = {
	steps: [
		{
			id: "import",
			eyebrow: "Import",
			icon: <CloudArrowUp />,
			image: "/app-assets/images/ink-import-field-sources.png",
			imageAlt: "Importing drone and field data into MangoMap",
			title: "Add Your Data",
			description:
				"Drop in Shapefile, KML, GeoJSON, CSV, and GeoTIFF. Your drone imagery, field surveys, remote sensing, and open data all come together in one map.",
		},
		{
			id: "design",
			eyebrow: "Design",
			icon: <MapTrifold />,
			image: "/app-assets/images/ink-style-editor.png",
			imageAlt: "Crafting an environmental map portal in MangoMap",
			title: "Craft Your Portal",
			description:
				"Build it with no-code styling: filters, symbology, and popups. Make a complex dataset legible to someone who is not a GIS analyst.",
		},
		{
			id: "distribute",
			eyebrow: "Distribute",
			icon: <ArrowBendDoubleUpRight />,
			image: "/app-assets/images/ink-share-link-pricing.png",
			imageAlt: "Sharing an environmental map with funders and clients",
			title: "Share the Link",
			description:
				"Publish to a link or embed it in your report or site. Funders and clients open it in a browser, with nothing to install.",
		},
		{
			id: "update",
			eyebrow: "Update",
			icon: <ArrowsClockwise />,
			image: "/app-assets/images/ink-map-update-before-after.png",
			imageAlt: "Updated environmental data reflected in a live map",
			title: "Keep It Current",
			description:
				"Add new data as the project runs. Every published map updates right away, so stakeholders never see a stale version.",
		},
		{
			id: "organize",
			eyebrow: "Organize",
			icon: <Layout />,
			image: "/app-assets/images/ink-maps-portal-team-pricing.png",
			imageAlt: "Managing environmental projects and access in MangoMap",
			title: "Stay Organised",
			description:
				"Keep every project and portal in one place. Add your team and set who can see each map.",
		},
		{
			id: "handoff",
			eyebrow: "Handoff",
			icon: <Handshake />,
			image: "/app-assets/images/ink-map-published-view-pricing.png",
			imageAlt: "Stakeholders exploring a published environmental portal",
			title: "Hand It Off",
			description:
				"Send the link and step back. Stakeholders explore the map on their own, and the questions stop landing on your desk.",
		},
	],
};

const faqs: FaqsContent = {
	faqContent: {
		eyebrow: "FAQ",
		heading: "Need more answers?",
		subHeading: (
			<Typography color="text.secondary">
				Check our&nbsp;
				<Box
					component="a"
					href="https://help.mangomap.com/hc/en-us/sections/360007615811-Frequently-Asked-Questions"
					sx={{ color: "inherit", textDecoration: "underline" }}>
					complete FAQ
				</Box>
				&nbsp;in the help center, or&nbsp;
				<Box
					component="a"
					href="mailto:sales@mangomap.com"
					sx={{ color: "inherit", textDecoration: "underline" }}>
					contact us
				</Box>
				&nbsp;directly.
			</Typography>
		),
	},
	faqs: [
		{
			id: "FAQ-1",
			question: "How does pricing work with grant-funded budgets?",
			answer:
				"Plans are fixed and predictable, so you can write the cost into a grant budget and not get surprised mid-project. No metered usage, no charges that move once the work is underway.",
		},
		{
			id: "FAQ-2",
			question: "Is it really no-code, even though I am technical?",
			answer:
				"Yes. You build and publish the portal without writing code or standing up a server, which keeps your time on the analysis and the fieldwork rather than the web stack.",
		},
		{
			id: "FAQ-3",
			question: "Does it work with drone and remote sensing data?",
			answer:
				"Yes. Bring in your drone imagery and the layers you produce from your remote sensing and field work, then publish them as a portal funders and clients can open. The heavy processing stays in your own tools. Mango is where the finished map goes online.",
		},
		{
			id: "FAQ-4",
			question:
				"Will non-technical stakeholders actually understand the portal?",
			answer:
				"Funders, clients, and community members open a link and explore the map on any device, with no login or GIS software. Making the work legible to them is the whole reason to publish it this way.",
		},
		{
			id: "FAQ-5",
			question: "How fast can I publish against a deadline?",
			answer:
				"From data to a live portal in hours or days, not weeks. Import, style, publish. No build queue, no bottleneck.",
		},
		{
			id: "FAQ-6",
			question: "Does it support long-term projects with ongoing updates?",
			answer:
				"Yes. Refresh the portal with new drone flights, field observations, or research as the project runs. The published map reflects the update, so it stays current across the funding period.",
		},
		{
			id: "FAQ-7",
			question: "Do I still need my desktop GIS?",
			answer:
				"Yes, and that is fine. Your analysis stays in the tools you know. Mango is the publishing layer that gets the finished map online for the people who do not have GIS software.",
		},
	],
};

const startBuilding: StartBuildingContent = {
	heading: "Get your environmental maps off the PDF and in front of funders",
	subHeading:
		"Bring drone imagery, field data, and open sources into one portal funders and clients open in a browser. No developer, no server, and pricing that fits a grant budget.",
	ctaButton: "Start Your Free Trial",
	ctaFooter:
		"You are collecting the data and making the case for the work. The publishing part should not fight you. Start a free trial, no credit card, and see how your next map looks as a portal.",
	image: "/app-assets/images/cta-map.jpg",
};

const featureHighlights: FeatureHighlightsContent = {
	heading: "Everything between the data and the link.",
	features: [
		{
			eyebrow: "Import",
			title: "Vector and Raster Support",
			description:
				"Shapefile, MapInfo Tab, KML, CSV, FileGeodatabase, GeoJSON, and GeoTIFF. Bring your vector layers and your drone imagery alike.",
			icon: (
				<FilePlus
					color="var(--mui-palette-secondary-main)"
					size={22}
					weight="duotone"
				/>
			),
		},
		{
			eyebrow: "Filter",
			title: "Ask Your Data Anything",
			description:
				"Build powerful filters on any field - without writing a line of SQL.",
			icon: (
				<ListMagnifyingGlass
					color="var(--mui-palette-secondary-main)"
					size={22}
					weight="duotone"
				/>
			),
		},
		{
			eyebrow: "Style",
			title: "Advanced Symbology Builder",
			description:
				"Thematic, category, heatmap, and custom graphic styles. Built once, reused across every map.",
			icon: (
				<PaintBrush
					color="var(--mui-palette-secondary-main)"
					size={22}
					weight="duotone"
				/>
			),
		},
		{
			eyebrow: "Share",
			title: "Share with Anyone",
			description:
				"Public links for clients, login-required links for internal audiences. You decide who sees what.",
			icon: (
				<Link
					color="var(--mui-palette-secondary-main)"
					size={22}
					weight="duotone"
				/>
			),
		},
		{
			eyebrow: "Brand",
			title: "Fully Branded Maps",
			description:
				"Custom domain, logo, and colours, so every map you publish looks like it came from you, not us.",
			icon: (
				<Tag
					color="var(--mui-palette-secondary-main)"
					size={22}
					weight="duotone"
				/>
			),
		},
		{
			eyebrow: "Analyze",
			title: "Spatial Analysis",
			description:
				"Proximity, buffer, and intersection. Run the analyses your clients need without opening desktop GIS software.",
			icon: (
				<Globe
					color="var(--mui-palette-secondary-main)"
					size={22}
					weight="duotone"
				/>
			),
		},
	],
};

const pricingTeaser: PricingTeaserContent = {
	heading: "The publishing job shouldn't cost enterprise money.",
	subHeading: "Fixed price plans. No surprises.",
	ctaLabel: "See pricing",
};

export const environmentContent = {
	hero,
	benefits,
	useCases,
	testimonials,
	howItWorks,
	faqs,
	featureHighlights,
	pricingTeaser,
	startBuilding,
};
